import React, { createContext, useContext } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import type { PaletteMode } from '@mui/material';

interface ThemeContextType {
  mode: PaletteMode;
  toggleColorMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

interface ThemeContextProviderProps {
  children: React.ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  // Tema sempre claro - não pode ser alterado
  const mode: PaletteMode = 'light';

  // Função desabilitada - não faz nada
  const toggleColorMode = () => {
    // Não faz nada - tema sempre claro
  };

  // Tema claro
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          // Cores principais inspiradas na Noname
          primary: {
            main: '#6366f1', // Indigo vibrante (cor principal)
            light: '#818cf8',
            dark: '#4f46e5',
            contrastText: '#ffffff',
          },
          secondary: {
            main: '#14b8a6', // Teal
            light: '#2dd4bf',
            dark: '#0d9488',
            contrastText: '#ffffff',
          },
          success: {
            main: '#10b981', // Verde para sucesso
            light: '#34d399',
            dark: '#059669',
          },
          warning: {
            main: '#f59e0b',
            light: '#fbbf24',
            dark: '#d97706',
          },
          error: {
            main: '#ef4444',
            light: '#f87171',
            dark: '#dc2626',
          },
          info: {
            main: '#14b8a6',
            light: '#2dd4bf',
            dark: '#0d9488',
          },
          background: {
            default: '#eef8f4', // Verde menta suave (estética saúde)
            paper: '#ffffff', // Branco (cards/superfícies)
          },
          text: {
            primary: '#475569', // Cinza médio-escuro para texto principal
            secondary: '#64748b', // Cinza médio para texto secundário
            disabled: '#94a3b8',
          },
          divider: 'rgba(148, 163, 184, 0.2)', // Divisor sutil
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: '#ffffff',
                color: '#1e293b',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: '#ffffff',
                borderRight: '1px solid rgba(148, 163, 184, 0.2)',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: '#ffffff',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  borderColor: 'rgba(148, 163, 184, 0.3)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.15)',
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: '#ffffff',
                border: '1px solid rgba(148, 163, 184, 0.2)',
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: 'rgba(148, 163, 184, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(148, 163, 184, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6366f1',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748b',
                  '&.Mui-focused': {
                    color: '#6366f1',
                  },
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: '0.5rem',
                fontWeight: 500,
                padding: '0.625rem 1.25rem',
              },
              contained: {
                background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
                boxShadow: '0 4px 14px 0 rgba(20, 184, 166, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0D9488 0%, #047857 100%)',
                  boxShadow: '0 6px 20px 0 rgba(20, 184, 166, 0.4)',
                },
              },
              outlined: {
                borderColor: 'rgba(148, 163, 184, 0.3)',
                color: '#1e293b',
                '&:hover': {
                  borderColor: '#14B8A6',
                  backgroundColor: 'rgba(20, 184, 166, 0.1)',
                },
              },
            },
          },
          MuiListItem: {
            styleOverrides: {
              root: {
                borderRadius: '0.5rem',
                margin: '0.25rem 0.5rem',
                '&:hover': {
                  backgroundColor: 'rgba(148, 163, 184, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                  borderLeft: '3px solid #6366f1',
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.2)',
                  },
                  '& .MuiListItemIcon-root': {
                    color: '#6366f1',
                  },
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                backgroundColor: 'rgba(148, 163, 184, 0.1)',
                color: '#1e293b',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                '&.MuiChip-colorPrimary': {
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                  color: '#6366f1',
                  borderColor: '#6366f1',
                },
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                color: '#64748b',
                '&:hover': {
                  backgroundColor: 'rgba(148, 163, 184, 0.1)',
                  color: '#1e293b',
                },
              },
            },
          },
        },
        typography: {
          fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 700,
          },
          h2: {
            fontWeight: 600,
          },
          h3: {
            fontWeight: 600,
          },
          h4: {
            fontWeight: 600,
          },
          h5: {
            fontWeight: 600,
          },
          h6: {
            fontWeight: 600,
          },
          button: {
            fontWeight: 500,
          },
        },
        shape: {
          borderRadius: 8,
        },
      }),
    [] // Não depende de nenhum estado - sempre o mesmo tema claro
  );

  const value = {
    mode,
    toggleColorMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}; 